import React, { useEffect, useImperativeHandle, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import SaveBtn from '@/features/settings/components/SaveBtn'
import { useCrmPermissions } from '../hooks/useCrmPermissions'
import ActionsSection from './ActionsSection'
import FieldPermissionsSection from './FieldPermissionsSection'
import type {
  ActionDecision,
  CrmPermissions,
  CrmActionKey,
  CrmObjectType,
} from '../types'

type Props = {
  workspaceId: string
  canManage: boolean
  onDirtyChange?: (dirty: boolean) => void
}

export type CrmPermissionsSectionHandle = {
  save: () => Promise<void>
  discard: () => void
}

const CrmPermissionsSection = React.forwardRef<CrmPermissionsSectionHandle, Props>(
  ({ workspaceId, canManage, onDirtyChange }, ref) => {
    const { currentTheme: t } = useTheme()
    const { permissions, loading, saving, error, setError, save } = useCrmPermissions(workspaceId)
    const [draft, setDraft] = useState<CrmPermissions | null>(null)
    const [savedMessage, setSavedMessage] = useState(false)

    useEffect(() => {
      if (permissions) setDraft(permissions)
    }, [permissions])

    const isDirty = !!permissions && !!draft && JSON.stringify(draft) !== JSON.stringify(permissions)

    useEffect(() => {
      onDirtyChange?.(isDirty)
    }, [isDirty, onDirtyChange])

    // Clear the dirty flag on unmount so a stale "true" doesn't linger if the
    // parent keeps state around after this section is no longer rendered.
    useEffect(() => () => onDirtyChange?.(false), [onDirtyChange])

    const handleSave = async () => {
      if (!draft) return
      setSavedMessage(false)
      try {
        await save({
          actions: draft.actions,
          fieldPermissions: draft.fieldPermissions,
        })
        setSavedMessage(true)
        setTimeout(() => setSavedMessage(false), 3000)
      } catch {
        // error is surfaced via the hook's `error` state
      }
    }

    useImperativeHandle(ref, () => ({
      save: handleSave,
      discard: () => { if (permissions) setDraft(permissions) },
    }))

    if (loading || !draft) {
      return (
        <div className="flex justify-center py-16">
          <Loader2 size={20} className="animate-spin" style={{ color: t.mutedText }} />
        </div>
      )
    }

    const disabled = !canManage

    const updateAction = (key: CrmActionKey, value: ActionDecision) => {
      setDraft((prev) => (prev ? { ...prev, actions: { ...prev.actions, [key]: value } } : prev))
    }

    const updateFieldPermissions = (objectType: CrmObjectType, deny: string[]) => {
      setDraft((prev) =>
        prev
          ? { ...prev, fieldPermissions: { ...prev.fieldPermissions, [objectType]: { deny } } }
          : prev,
      )
    }

    return (
      <div className="flex flex-col gap-8">
        {!canManage && (
          <p className="text-[12.5px] rounded-lg px-3 py-2" style={{ backgroundColor: t.hoverBackground, color: t.mutedText }}>
            Only the workspace owner can view or change what Zeph is allowed to do automatically.
          </p>
        )}

        <div>
          <h2 className="text-sm font-bold mb-1" style={{ color: t.text }}>What Zeph can do on its own</h2>
          <p className="text-[12.5px] mb-3" style={{ color: t.mutedText }}>
            Auto runs the action immediately. Ask first pauses for your approval before running it. Off removes it entirely — Zeph won't even offer to do it.
          </p>
          <ActionsSection actions={draft.actions} onChange={updateAction} {...(disabled ? { disabled } : {})} />
        </div>

        <div>
          <h2 className="text-sm font-bold mb-1" style={{ color: t.text }}>Fields Zeph can never write</h2>
          <p className="text-[12.5px] mb-3" style={{ color: t.mutedText }}>
            Pick from common fields, or block a custom property by its internal name — Zeph will never write to it, no matter what's asked.
          </p>
          <FieldPermissionsSection
            fieldPermissions={draft.fieldPermissions}
            onChange={updateFieldPermissions}
            {...(disabled ? { disabled } : {})}
          />
        </div>

        {canManage && (
          <div className="flex items-center gap-3">
            <SaveBtn label={saving ? 'Saving…' : 'Save changes'} onClick={() => { if (!saving) void handleSave() }} />
            {isDirty && !saving && <span className="text-[12.5px]" style={{ color: t.mutedText }}>Unsaved changes</span>}
            {savedMessage && <span className="text-[12.5px]" style={{ color: t.successText }}>Saved</span>}
            {error && (
              <span className="text-[12.5px]" style={{ color: t.errorText }}>
                {error}
                <button type="button" className="ml-2 underline" onClick={() => setError(null)}>dismiss</button>
              </span>
            )}
          </div>
        )}
      </div>
    )
  },
)

export default React.memo(CrmPermissionsSection)
